/**
 * `estimates.ts` — the coarse-bucket estimate vocabulary + the read-time
 * sum-of-leaves roll-up (#1043, B2.12 / Track-B). Pure + dependency-free
 * (apart from the tree types), so it is fully unit-testable and reused by the
 * drawer, the card, and the parent roll-up alike.
 *
 * Brand firewall (§A1.2 / §A9): an estimate is a COARSE forward bucket — a
 * rough SIZE for a piece of work, never a contract, a per-person commitment, or
 * a deadline tally. It is set on a leaf and the parent roll-up SUMS the leaves
 * — about the WORK, never anyone — and is computed HERE at read time, never
 * stored. There is deliberately no per-person aggregation anywhere in this
 * module; the roll-up takes a `TreeNode` (a subtree of work) and knows nothing
 * about who is on it.
 *
 * Unit convention (load-bearing, documented once):
 *   1 working day = 8 hours = 480 minutes.
 *   1 working week = 5 days   = 2400 minutes.
 * The wire stores raw minutes (the backend is bucket-agnostic), so the bucket
 * vocabulary can grow here without a backend change.
 */

import type { TreeNode } from "./buildTree";

/** One working day in minutes (8h × 60). The base unit the buckets build on. */
export const MINUTES_PER_DAY = 480;
/** One working week in minutes (5 working days). */
export const MINUTES_PER_WEEK = MINUTES_PER_DAY * 5; // 2400

/** A coarse estimate bucket the UI offers — a label + its minute value. */
export interface EstimateBucket {
  /** The chip label (the wireframe's mono caption, e.g. "½d", "1w"). */
  label: string;
  /** The bucket's value in minutes (the wire unit). */
  minutes: number;
}

/**
 * The coarse bucket ladder (the wireframe `V_B212` chips, in order):
 *   ½d=240 · 1d=480 · 2d=960 · 3d=1440 · 1w=2400 · 2w=4800.
 * "custom…" is NOT a fixed bucket — it is a free days input the drawer adds
 * after these, so it lives in the UI, not this ladder.
 */
export const ESTIMATE_BUCKETS: readonly EstimateBucket[] = [
  { label: "½d", minutes: MINUTES_PER_DAY / 2 }, // 240
  { label: "1d", minutes: MINUTES_PER_DAY }, // 480
  { label: "2d", minutes: MINUTES_PER_DAY * 2 }, // 960
  { label: "3d", minutes: MINUTES_PER_DAY * 3 }, // 1440
  { label: "1w", minutes: MINUTES_PER_WEEK }, // 2400
  { label: "2w", minutes: MINUTES_PER_WEEK * 2 }, // 4800
] as const;

/**
 * Format a minute estimate as a coarse `~Xunit` label (the wireframe's `~3d` /
 * `~6w` / `~½d`), choosing the COARSEST sensible unit:
 *   - an exact half-day (and < 1 day) reads `~½d`;
 *   - a whole-week multiple reads `~Nw` (weeks are coarser than days);
 *   - otherwise it reads `~Nd` (rounded to the nearest working day, min 1).
 *
 * Returns `null` for a null / non-positive value, so a caller can render the
 * shared "— (optional)" empty state when there is no estimate. The leading `~`
 * is the brand's reminder that this is approximate — a coarse bucket, never a
 * precise contract (§A1.2).
 */
export function formatEstimate(minutes: number | null | undefined): string | null {
  if (minutes == null || minutes <= 0) return null;

  // A whole number of working weeks reads in weeks (the coarsest unit).
  if (minutes % MINUTES_PER_WEEK === 0) {
    return `~${minutes / MINUTES_PER_WEEK}w`;
  }

  // Exactly half a working day (and under a full day) reads `~½d`.
  if (minutes === MINUTES_PER_DAY / 2) {
    return "~½d";
  }

  // Otherwise express in working days, rounded to the nearest day (min 1 so a
  // small sub-day value never collapses to "~0d").
  const days = Math.max(1, Math.round(minutes / MINUTES_PER_DAY));
  return `~${days}d`;
}

/** The sum-of-leaves estimate roll-up for one subtree (computed, never stored). */
export interface EstimateRollup {
  /** Sum of every LEAF's estimate (null leaves contribute 0). */
  totalMinutes: number;
  /** Sum of every NOT-`done` leaf's estimate (null leaves contribute 0). */
  remainingMinutes: number;
}

/**
 * The read-time estimate roll-up for an outcome / parent subtree: the SUM of
 * the LEAF estimates beneath `node` (a leaf is a node with zero children — the
 * smallest unit of work). `remainingMinutes` sums only the leaves that are NOT
 * yet `done`, so a parent can read "~6w total · ~1.5w remaining" (the wireframe
 * `V_B212`). A leaf with no estimate contributes 0 to both sums.
 *
 * Why LEAVES only: an estimate is set on the small concrete pieces of work; a
 * parent's size is the sum of its parts, never a separately-entered number on
 * the parent. Summing every node (parents too) would double-count. A lone node
 * with no children is itself a leaf, so its own estimate (if any) is the whole
 * roll-up.
 *
 * Pure + about the WORK, never a person (§A6 / §A9): it reads each leaf's
 * `estimateMinutes` + `state` and nothing about who is on it. Computed here at
 * read time — the result is display-only and is never persisted.
 */
export function estimateRollup(node: TreeNode): EstimateRollup {
  let totalMinutes = 0;
  let remainingMinutes = 0;

  const walk = (n: TreeNode): void => {
    if (n.children.length === 0) {
      const minutes = n.estimateMinutes ?? 0;
      totalMinutes += minutes;
      if (n.state !== "done") remainingMinutes += minutes;
      return;
    }
    for (const child of n.children) walk(child);
  };
  walk(node);

  return { totalMinutes, remainingMinutes };
}
